import { HubConnection } from "@microsoft/signalr";
import React, { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useEffect, useState } from "react";
import { BearerTokenProvider } from "sonobello.utilities.react.axios";

import { HandledRequestError } from "../Components/ErrorHandling/ErrorHandler";
import { Token } from "../Models/Authentication";
import { BusinessUnit } from "../Models/BusinessUnit";
import { Center, EnhancedCenter } from "../Models/Center";
import { CustomDimension } from "../Models/CustomDimension";
import { DisqualifyingReason } from "../Models/DisqualifyingReason";
import { DynamicContent } from "../Models/DynamicContent";
import { EnhancedIvrState, IvrState } from "../Models/IvrState";
import { User } from "../Models/User";
import { LogOutProtocol } from "../Types/LogOutProtocol";
import { Notification } from "../Types/Notification";
import { Error } from "../Utils/Errors";
import { LocalStorageConfigs, loadFromLocal, saveToLocal } from "../Utils/LocalStorage";
import { useApi } from "../Utils/useApi";
import { AreaOfInterest } from "../V2/Playbook/Interests/Types/AreaOfInterest";

interface SignalRConnectionInfo {
  url: string;
  accessToken: string;
}

export interface UserContextProps {
  areasOfInterest: AreaOfInterest[];
  /** Flag indicating that user's authentication is no longer valid and should be manually re-created. */
  showReauthenticator: boolean;
  bearerTokenProvider: BearerTokenProvider;
  businessUnits: BusinessUnit[];
  centers: EnhancedCenter[];
  disqualifyingReasons: DisqualifyingReason[];
  dynamicContent?: DynamicContent[];
  handledError?: HandledRequestError;
  ivrState: IvrState;
  leadResultTypes: CustomDimension<number>[];
  loginLeadId: string;
  logOutProtocol?: LogOutProtocol;
  notOfferedProcedures: CustomDimension<string>[];
  notifications: Notification[];
  signalR: {
    payloads: Record<string, unknown | null>;
    srConnection?: HubConnection;
    srConnectionInfo?: SignalRConnectionInfo;
    setTopics: Dispatch<SetStateAction<{ name: string; count: number }[]>>;
    setSrConnection: Dispatch<SetStateAction<HubConnection | undefined>>;
    setSrConnectionInfo: Dispatch<SetStateAction<SignalRConnectionInfo | undefined>>;
    setPayloads: Dispatch<SetStateAction<Record<string, unknown | null>>>;
    subscribe: (topics: string[]) => void;
    unsubscribe: (topics: string[]) => void;
  };
  token?: Token;
  user: User;

  setAreasOfInterest: Dispatch<SetStateAction<AreaOfInterest[]>>;
  /** Update the state of the {@link showReauthenticator} flag. */
  setShowReauthenticator: Dispatch<boolean>;
  setBusinessUnits: Dispatch<SetStateAction<BusinessUnit[]>>;
  setCenters: Dispatch<SetStateAction<EnhancedCenter[]>>;
  setDisqualifyingReasons: Dispatch<SetStateAction<DisqualifyingReason[]>>;
  setDynamicContent: (content: DynamicContent[]) => void;
  setHandledError: Dispatch<SetStateAction<HandledRequestError | undefined>>;
  setIvrState: Dispatch<SetStateAction<EnhancedIvrState>>;
  setLeadResultTypes: Dispatch<SetStateAction<CustomDimension<number>[]>>;
  setLogOutProtocol: Dispatch<SetStateAction<LogOutProtocol | undefined>>;
  setLoginLeadId: Dispatch<SetStateAction<string>>;
  setNotifications: Dispatch<SetStateAction<Notification[]>>;
  setNotOfferedProcedures: Dispatch<SetStateAction<CustomDimension<string>[]>>;
  setShouldRenewToken: Dispatch<SetStateAction<boolean>>;
  setUser: Dispatch<SetStateAction<User>>;
  setToken: Dispatch<SetStateAction<Token | undefined>>;
}

export const UserContext = createContext({} as UserContextProps);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const [areasOfInterest, setAreasOfInterest] = useState(
    loadFromLocal<AreaOfInterest[]>(LocalStorageConfigs.areasOfInterest.key) || []
  );
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>(
    () => loadFromLocal<BusinessUnit[]>(LocalStorageConfigs.businessUnits.key) || []
  );
  const [centers, setCenters] = useState<EnhancedCenter[]>(() =>
    (loadFromLocal<Center[]>("centers") || []).map((c: Center) => new EnhancedCenter(c))
  );
  const [disqualifyingReasons, setDisqualifyingReasons] = useState<DisqualifyingReason[]>(
    () => loadFromLocal<DisqualifyingReason[]>(LocalStorageConfigs.disqualifyingReasons.key) || []
  );
  const [dynamicContent, setDynamicContent] = useState(() =>
    loadFromLocal<DynamicContent[]>(LocalStorageConfigs.dynamicContent.key)
  );
  const [handledError, setHandledError] = useState<HandledRequestError>();
  const [shouldRenewToken, setShouldRenewToken] = useState(false);
  const [ivrState, setIvrState] = useState<EnhancedIvrState>(() => ({ state: "Disconnected" }));
  const [leadResultTypes, setLeadResultTypes] = useState<CustomDimension<number>[]>(
    () => loadFromLocal<CustomDimension<number>[]>(LocalStorageConfigs.leadResultTypes.key) || []
  );
  const [loginLeadId, setLoginLeadId] = useState<string>("");
  const [logOutProtocol, setLogOutProtocol] = useState<LogOutProtocol>();
  const [notOfferedProcedures, setNotOfferedProcedures] = useState<CustomDimension<string>[]>(
    () => loadFromLocal<CustomDimension<string>[]>(LocalStorageConfigs.notOfferedProcedures.key) || []
  );
  const [notifications, setNotifications] = useState(
    () => loadFromLocal<Notification[]>(LocalStorageConfigs.notifications.key) || []
  );
  const [srConnection, setSrConnection] = useState<HubConnection>();
  const [srConnectionInfo, setSrConnectionInfo] = useState(() =>
    loadFromLocal<SignalRConnectionInfo>(LocalStorageConfigs.signalRConnectionInfo.key)
  );
  const [srPayloads, setPayloads] = useState<Record<string, unknown | null>>({});
  const [, setTopics] = useState<{ name: string; count: number }[]>([]);
  const [token, setToken] = useState(() => loadFromLocal<Token>(LocalStorageConfigs.token.key));
  const [bearerTokenProvider] = useState(new BearerTokenProvider(token?.token || "", () => null));
  const [user, setUser] = useState(() => loadFromLocal<User>(LocalStorageConfigs.user.key) || new User());
  const [showReauthenticator, setShowReauthenticator] = useState(false);

  // RENEW AUTH TOKEN
  const { response: tokenResponse, error: tokenError } = useApi<Token, Token>({
    method: "put",
    url: "Legacy/V0/Token",
    config: { headers: { Authorization: `Bearer ${token?.token}` } },
    data: token,
    valid: shouldRenewToken,
    error: Error.PutToken
  });
  useEffect(() => {
    if (tokenResponse) {
      setShouldRenewToken(false);
      setToken(tokenResponse);
      localStorage.setItem(LocalStorageConfigs.token.key, JSON.stringify(tokenResponse));
    }
  }, [tokenResponse]);
  useEffect(() => token && bearerTokenProvider.updateTokenAndRetryUnauthorizedRequests(token.token), [token?.token]);
  useEffect(() => tokenError && setLogOutProtocol("error"), [tokenError]);

  // Cache context values on change
  useEffect(() => saveToLocal(areasOfInterest, LocalStorageConfigs.areasOfInterest.key), [areasOfInterest]);
  useEffect(() => saveToLocal(businessUnits, LocalStorageConfigs.businessUnits.key), [businessUnits]);
  useEffect(
    () => saveToLocal(disqualifyingReasons, LocalStorageConfigs.disqualifyingReasons.key),
    [disqualifyingReasons]
  );
  useEffect(
    () => dynamicContent && saveToLocal(dynamicContent, LocalStorageConfigs.dynamicContent.key),
    [dynamicContent]
  );
  useEffect(() => saveToLocal(centers, LocalStorageConfigs.centers.key), [centers]);
  useEffect(() => saveToLocal(leadResultTypes, LocalStorageConfigs.leadResultTypes.key), [leadResultTypes]);
  useEffect(() => saveToLocal(notifications, LocalStorageConfigs.notifications.key), [notifications]);
  useEffect(
    () => saveToLocal(notOfferedProcedures, LocalStorageConfigs.notOfferedProcedures.key),
    [notOfferedProcedures]
  );
  useEffect(() => token && saveToLocal(token, LocalStorageConfigs.token.key), [token]);
  useEffect(() => (user.id ? saveToLocal(user, LocalStorageConfigs.user.key) : undefined), [user]);

  // SIGNAL R HELPERS
  const subscribe = useCallback(
    (topics: string[]) =>
      srConnection &&
      setTopics(t => {
        const newTopics: string[] = [];
        topics.forEach(topic => {
          const index = t.findIndex(({ name }) => name === topic);
          if (index !== -1) t[index].count++;
          else {
            t.push({ name: topic, count: 1 });
            newTopics.push(topic);
          }
        });
        if (newTopics.length)
          setPayloads(payloads => {
            newTopics.forEach(topic => (payloads[topic] = null));
            return { ...payloads };
          });
        newTopics.map(topic =>
          srConnection.on(topic, data => setPayloads(payloads => ({ ...payloads, [topic]: data })))
        );
        return t;
      }),
    [srConnection]
  );
  const unsubscribe = useCallback(
    (topics: string[]) =>
      srConnection &&
      setTopics(t => {
        const removeTopics: string[] = [];
        topics.forEach(topic => {
          const index = t.findIndex(({ name }) => name === topic);
          if (index !== -1 && --t[index].count === 0) removeTopics.push(t.splice(index, 1)[0].name);
        });
        if (removeTopics.length)
          setPayloads(payloads => {
            removeTopics.forEach(topic => delete payloads[topic]);
            return { ...payloads };
          });
        removeTopics.forEach(topic => srConnection.off(topic));
        return t;
      }),
    [srConnection, setTopics]
  );

  return (
    <UserContext.Provider
      value={{
        areasOfInterest,
        bearerTokenProvider,
        businessUnits,
        centers,
        disqualifyingReasons,
        handledError,
        ivrState,
        leadResultTypes,
        loginLeadId,
        logOutProtocol,
        notOfferedProcedures,
        notifications,
        dynamicContent,
        signalR: {
          payloads: srPayloads,
          srConnection,
          srConnectionInfo,
          setTopics,
          setSrConnection,
          setSrConnectionInfo,
          setPayloads,
          subscribe,
          unsubscribe
        },
        showReauthenticator,
        token,
        user,

        setAreasOfInterest,
        setBusinessUnits,
        setDisqualifyingReasons,
        setIvrState,
        setLeadResultTypes,
        setNotifications,
        setNotOfferedProcedures,
        setDynamicContent,
        setToken,
        setUser,
        setCenters,
        setHandledError,
        setLoginLeadId,
        setLogOutProtocol,
        setShouldRenewToken,
        setShowReauthenticator
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
